import React, { memo, useState, useMemo } from 'react';
import { classnames } from '@core/utils/css';
import { useCourseBuilderContext } from '../../CourseBuilderContext';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { FormText } from '@core/components/form/FormText';
import { openCourseBuilderSettingsOverlay } from '../CourseBuilderSettingsOverlay';
import { toMoment } from '@core/utils/time';
import { useHistory, useQueryStringParam } from '@core/router';
import { Button } from '@core/components/Button';
import { CourseId, CourseStepResponse } from '@business/entities/course';
import { AssistantButton } from '@modules/common';
import { AIToolResponse } from '@business/constants/ai';
import { Observable } from 'rxjs';
import OpenAI from 'openai';
import { LIST_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/ListBlock';
import { MULTIMEDIA_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/MultimediaBlock';
import { TEXT_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/TextBlock';
import { IMAGE_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/ImageBlock';
import { DIVIDER_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/DividerBlock';
import { STATEMENT_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/StatementBlock';
import { QUOTE_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/QuoteBlock';
import { GALLERY_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/GalleryBlock';
import { DRILL_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/DrillBlock';
import { TODOLIST_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/TodoListBlock';
import { ACCORDION_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/AccordionBlock';
import { TABS_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/TabsBlock';
import { PROCESS_BLOCK_AI_INSTRUCTION } from '@modules/course/CourseBuilder/Blocks/ProcessBlock';
import './index.scss';

interface CourseBuilderHeaderProps {
  lastSaved?: number | null;
  canSave: boolean;
  onSave: () => void;
  id: string;
  courseId?: CourseId;
  onAIOutput?: (output: string) => void;
  tools?: OpenAI.Responses.Tool[];
  onToolCall?(callId: string, fnName: string, args: any): Observable<AIToolResponse>;
  lesson: CourseStepResponse;
}

export const CourseBuilderHeader = memo((props: CourseBuilderHeaderProps) => {
  const { courseId, tools, onToolCall, onAIOutput, lesson } = props;
  const returnUrl = useQueryStringParam('returnUrl') || courseId ? `/courses/manage/${courseId}/steps` : '/courses';
  const [isOpen, setOpen] = useState(false);
  const { toggleSidebar, form } = useCourseBuilderContext()!;
  const formValue = useNonNilObservable(form.value$);
  const history = useHistory();

  const blockInstructions = useMemo(
    () => [
      LIST_BLOCK_AI_INSTRUCTION,
      MULTIMEDIA_BLOCK_AI_INSTRUCTION,
      TEXT_BLOCK_AI_INSTRUCTION,
      IMAGE_BLOCK_AI_INSTRUCTION,
      DIVIDER_BLOCK_AI_INSTRUCTION,
      STATEMENT_BLOCK_AI_INSTRUCTION,
      QUOTE_BLOCK_AI_INSTRUCTION,
      GALLERY_BLOCK_AI_INSTRUCTION,
      DRILL_BLOCK_AI_INSTRUCTION,
      TODOLIST_BLOCK_AI_INSTRUCTION,
      ACCORDION_BLOCK_AI_INSTRUCTION,
      TABS_BLOCK_AI_INSTRUCTION,
      PROCESS_BLOCK_AI_INSTRUCTION,
    ],
    [],
  );

  return (
    <div className={classnames('course-builder-header', { open: isOpen })}>
      <div className="bar-handle" onClick={() => history.push(returnUrl)}>
        <i className="fa fa-arrow-left"></i>
      </div>
      <div className="bar-handle" onClick={toggleSidebar}>
        <i className="fas fa-bars"></i>
      </div>
      <div className="proposal-title">
        <FormText control={form.controls.title} singleLine editable toolbar={false} />
      </div>
      <div className="flex-fill"></div>
      <div className="proposal-header-right">
        <div className="proposal-settings-icon" onClick={() => openCourseBuilderSettingsOverlay({ form })}>
          <i className="fas fa-cog mr-2"></i>
        </div>
        {!!props.lastSaved && <div className="mr-2 text-muted">Last saved {toMoment(props.lastSaved).format('hh:mma')}</div>}
        <AssistantButton
          clasName="mr-2"
          assistantId={'19'}
          tools={tools}
          onToolCall={onToolCall}
          onDone={onAIOutput}
          onOutputMessage={message => {
            const blocksInstructions = blockInstructions.join('\n');
            const replacedMessage = message.replace(/{{BLOCKS_INSTRUCTIONS}}/g, blocksInstructions);
            return replacedMessage;
          }}
          onInitialMessage={message => {
            const replacedMessage = message.replace(/{{Step_Name}}/g, lesson.name);
            return replacedMessage;
          }}
        >
          Generate With AI
        </AssistantButton>
        <Button onClick={props.onSave} disabled={!props.canSave}>
          Save
        </Button>

        <a
          href={courseId ? `/courses/preview/${courseId}?step=${props.id}` : `/courses/lesson/preview/${props.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary ml-2"
        >
          Preview
        </a>
      </div>
    </div>
  );
});
